/**
 * Who publishes this game and where it lives.
 *
 * One source of truth for the wordmark, the copyright line and the list of
 * games, so a new game is added here and appears everywhere the game itself
 * links out. The static pages under `site/` are written by hand and have to be
 * updated alongside this list. See WEBSITE.md.
 *
 * Nothing here causes a network request. The list is baked into the bundle at
 * build time on purpose, because the game has to keep working with no network
 * at all: installed to a phone home screen, wrapped for a store, or running
 * from a folder on a memory stick.
 */

export const SITE = {
  name: 'Uphill Games',
  url: 'https://uphill-games.com',
  year: 2026,
  /**
   * Kept short on purpose: this renders in the game's own footer, inside the
   * canvas HUD, where the full "code under MIT, games under CC BY-SA" line would
   * not fit. The word links to the licence page, which spells it out. The site
   * pages, which have room, state both licences in full.
   */
  rights: 'Some rights reserved',
} as const;

/** The licence page, linked from every footer. */
export const LICENCE_URL = 'https://uphill-games.com/licence/';

export interface GameLink {
  /** The path under the site root, with no slashes. */
  slug: string;
  title: string;
  /** One line, for a menu. Plain English, no dashes. */
  blurb: string;
}

export const GAMES: readonly GameLink[] = [
  {
    slug: 'fire-rescue',
    title: 'Sierra Nevada Fire Rescue',
    blurb: 'A mountain on fire, one helicopter, and everybody has to get off.',
  },
];

/**
 * Whether the game may link out of itself.
 *
 * Game portals such as CrazyGames and Poki generally forbid links that take a
 * player off their page, so a build for one of those sets VITE_NO_OUTBOUND=1
 * and every link disappears. Nothing else changes.
 */
export const SHOW_SITE_LINKS = import.meta.env.VITE_NO_OUTBOUND !== '1';

/** An absolute URL into the site. Absolute because the game may be embedded. */
export const siteUrl = (path = ''): string => `${SITE.url}/${path}`;

export const COPYRIGHT = `© ${SITE.year} ${SITE.name}. ${SITE.rights}.`;

/** The copyright line with the rights phrase linked to the licence page. */
export const copyrightHtml = (): string =>
  `© ${SITE.year} ${SITE.name}. ${extLink(LICENCE_URL, SITE.rights)}.`;

/**
 * A link out of the game.
 *
 * Always a new tab. Inside an embed such as itch.io, an ordinary link loads the
 * whole website inside the host's small frame, and `_top` is usually blocked by
 * the frame's sandbox. A new tab works everywhere.
 */
export const extLink = (href: string, text: string, cls = 'ext'): string =>
  `<a class="${cls}" href="${href}" target="_blank" rel="noopener">${text}</a>`;
